/* =====================================================================
   AstaLab 2.0 — motore di calcolo (score, sotto-indici, fasce, scarsità,
   prezzi, inflazione), portato VERBATIM dalla reference implementation
   1.3.0-beta.6 (SHA256 47c145d0afd5b9ce9aefbf026e84a900482d98150c4d36afe7ec4f67cd27cb89),
   astalab.html righe 542-900, senza alcuna modifica di formula.

   Obiettivo 7 del round 2.0.0-alpha.1: "porta il motore applicativo in
   moduli separati senza cambiarne il comportamento" / "non riscrivere le
   formule". Ogni funzione qui sotto è un copia-incolla letterale del
   corpo originale — l'unica differenza è la rimozione dell'accoppiamento
   a STATE/DOM/localStorage (che nella 1.3 non esisteva comunque in queste
   funzioni: sono già pure, prendono solo argomenti ed effetti collaterali
   sugli oggetti player passati). Vedi test_engine_golden_130.js per la
   verifica byte-a-byte dei risultati contro la baseline reale.
   ===================================================================== */

export const ROLES = ["POR","DIF","CEN","ATT"];

export const TIER_ORDER = ["Elite","Top","Semitop","Titolare","Rotazione","Scommessa","Riserva"];

export const TIER_THRESHOLD_KEYS_DESC = ["Elite","Top","Semitop","Titolare","Rotazione","Scommessa"];
export const DEFAULT_TIER_THRESHOLDS = {Elite:90,Top:80,Semitop:70,Titolare:60,Rotazione:50,Scommessa:40};

export const DEFAULT_WEIGHTS = {
  fantamedia:25, mediaVoto:10, titolarita:15, minuti:10, bonusPartita:15,
  continuita:10, piazzatiRigori:5, scarsita:10,
  penInfortuni:8, penRotazione:6, penEspulsioni:3, penAutogol:2, penRigoriSbagliati:3, penMinutiBassi:5
};
export const POSITIVE_WEIGHT_KEYS = Object.keys(DEFAULT_WEIGHTS).filter(k=>!k.startsWith("pen"));

export const BUDGET_PRESETS = {
  prudente:   {POR:8,  DIF:20, CEN:32, ATT:32, riserva:8},
  equilibrato:{POR:6,  DIF:22, CEN:34, ATT:32, riserva:6},
  aggressivo: {POR:4,  DIF:16, CEN:30, ATT:46, riserva:4}
};

/* ---- utility numeriche identiche alla 1.3 (astalab.html righe 250-251) ---- */
export function clamp(n,a,b){ return Math.max(a, Math.min(b, n)); }
export function num(v, fallback){ const n = parseFloat(v); return isFinite(n) ? n : (fallback===undefined?0:fallback); }
export function round1(n){ return Math.round(n*10)/10; }

export function hasManualMaxPrice(p){ return p.prezzoMassimoManuale!==null && p.prezzoMassimoManuale!==undefined; }
export function effectiveMaxPrice(p){ return hasManualMaxPrice(p) ? p.prezzoMassimoManuale : p.__prezzoMassimo; }

export function roleGroupStats(players, ruolo){
  const g = players.filter(p=>p.ruolo===ruolo);
  function stats(arr){
    if(!arr.length) return {min:0,max:1};
    return {min:Math.min(...arr), max:Math.max(...arr)};
  }
  return {
    fantamedia: stats(g.map(p=>num(p.fantamedia))),
    mediaVoto: stats(g.map(p=>num(p.mediaVoto))),
    minuti: stats(g.map(p=>num(p.minuti))),
    bonusPartita: stats(g.map(p=> bonusPerPartita(p))),
    cleanSheetRate: stats(g.map(p=> p.partiteGiocate? num(p.cleanSheet)/num(p.partiteGiocate):0)),
    golSubitiRate: stats(g.map(p=> p.partiteGiocate? num(p.golSubiti)/num(p.partiteGiocate):0)),
    rigoriParati: stats(g.map(p=>num(p.rigoriParati))),
    count: g.length
  };
}

export function normIn(v, min, max){
  if(max<=min) return 50;
  return clamp(((v-min)/(max-min))*100, 0, 100);
}

export function bonusPerPartita(p){
  const pg = num(p.partiteGiocate);
  if(!pg) return 0;
  const bonusRaw = num(p.gol)*3 + num(p.assist)*1 + num(p.rigoriSegnati)*3;
  return bonusRaw/pg;
}

export function malusPerPartita(p){
  const pg = num(p.partiteGiocate);
  if(!pg) return 0;
  const malusRaw = num(p.ammonizioni)*0.5 + num(p.espulsioni)*1 + num(p.autogol)*2 + num(p.rigoriSbagliati)*3;
  return malusRaw/pg;
}

/* Calcola score 0-100 + sotto-indici per un giocatore, dato il pool ruolo */
export function computeScore(p, gstats, weights, config){
  const w = weights;
  const isPor = p.ruolo==="POR";
  let base;
  if(isPor){
    const nMV = normIn(num(p.mediaVoto), gstats.mediaVoto.min, gstats.mediaVoto.max);
    const nFM = normIn(num(p.fantamedia), gstats.fantamedia.min, gstats.fantamedia.max);
    const csRate = p.partiteGiocate? num(p.cleanSheet)/num(p.partiteGiocate) : 0;
    const nCS = normIn(csRate, gstats.cleanSheetRate.min, gstats.cleanSheetRate.max);
    const gsRate = p.partiteGiocate? num(p.golSubiti)/num(p.partiteGiocate) : 1;
    const nGS = 100-normIn(gsRate, gstats.golSubitiRate.min, gstats.golSubitiRate.max);
    const nRP = normIn(num(p.rigoriParati), gstats.rigoriParati.min, gstats.rigoriParati.max);
    const nTit = clamp(num(p.titolarita),0,100);
    const nCont = clamp(num(p.continuita),0,100);
    base = nFM*0.30 + nMV*0.15 + nTit*0.15 + nCS*0.15 + nGS*0.10 + nRP*0.05 + nCont*0.10;
  } else {
    const nFM = normIn(num(p.fantamedia), gstats.fantamedia.min, gstats.fantamedia.max);
    const nMV = normIn(num(p.mediaVoto), gstats.mediaVoto.min, gstats.mediaVoto.max);
    const nMin = normIn(num(p.minuti), gstats.minuti.min, gstats.minuti.max);
    const nBonus = normIn(bonusPerPartita(p), gstats.bonusPartita.min, gstats.bonusPartita.max);
    const nTit = clamp(num(p.titolarita),0,100);
    const nCont = clamp(num(p.continuita),0,100);
    const piazzatiScore = (p.rigorista?40:0) + (p.battitorePunizioni?30:0) + (p.battitoreCorner?30:0);
    const nPiazz = clamp(piazzatiScore,0,100);
    base = (nFM*w.fantamedia + nMV*w.mediaVoto + nTit*w.titolarita + nMin*w.minuti +
            nBonus*w.bonusPartita + nCont*w.continuita + nPiazz*w.piazzatiRigori) / 100;
  }

  const modDifesaBonus = (config && config.modDifesaAttivo && (p.ruolo==="DIF" || p.ruolo==="POR")) ? 6 : 0;
  base = base + modDifesaBonus;

  const scarcityIdx = p.__scarsitaScoreBoost || 0;
  let score = base + scarcityIdx*(w.scarsita/100);

  const malus = malusPerPartita(p);
  const nMalus = clamp(malus*8,0,100);
  const penInf = clamp(num(p.rischioInfortuni),0,100) * (w.penInfortuni/100);
  const penRot = clamp(num(p.rischioRotazione),0,100) * (w.penRotazione/100);
  const penEsp = clamp(num(p.espulsioni)*20,0,100) * (w.penEspulsioni/100);
  const penAuto = clamp(num(p.autogol)*25,0,100) * (w.penAutogol/100);
  const penRig = clamp(num(p.rigoriSbagliati)*20,0,100) * (w.penRigoriSbagliati/100);
  const minRate = p.partiteGiocate? num(p.minuti)/(num(p.partiteGiocate)*90) : 0;
  const penMin = clamp((1-minRate)*100,0,100) * (w.penMinutiBassi/100);

  score = score - (penInf+penRot+penEsp+penAuto+penRig+penMin)*0.3;
  score = clamp(score,0,100);

  const rendimento = isPor
    ? clamp(normIn(num(p.fantamedia), gstats.fantamedia.min, gstats.fantamedia.max),0,100)
    : clamp(normIn(num(p.fantamedia), gstats.fantamedia.min, gstats.fantamedia.max),0,100);
  const affidabilita = clamp((clamp(num(p.titolarita),0,100)+clamp(num(p.continuita),0,100))/2,0,100);
  const potenzialeBonus = isPor ? clamp(normIn(num(p.rigoriParati),gstats.rigoriParati.min,gstats.rigoriParati.max),0,100)
    : clamp(normIn(bonusPerPartita(p),gstats.bonusPartita.min,gstats.bonusPartita.max),0,100);
  const titolaritaIdx = clamp(num(p.titolarita),0,100);
  const rischioIdx = clamp((num(p.rischioInfortuni)+num(p.rischioRotazione))/2,0,100);
  const convenienza = clamp(score - (num(p.quotazione)||1)*2, 0, 100);

  return {
    score: round1(score),
    sub:{
      rendimento: round1(rendimento),
      affidabilita: round1(affidabilita),
      potenzialeBonus: round1(potenzialeBonus),
      titolarita: round1(titolaritaIdx),
      rischio: round1(rischioIdx),
      convenienza: round1(convenienza),
      scarsita: round1(scarcityIdx)
    }
  };
}

export function effectiveTier(score, thresholds){
  if(score>=thresholds.Elite) return "Elite";
  if(score>=thresholds.Top) return "Top";
  if(score>=thresholds.Semitop) return "Semitop";
  if(score>=thresholds.Titolare) return "Titolare";
  if(score>=thresholds.Rotazione) return "Rotazione";
  if(score>=thresholds.Scommessa) return "Scommessa";
  return "Riserva";
}

/* Calcola scarsità per ruolo: 0-100 (più alto = più scarso) */
export function computeScarcityByRole(players, config){
  const res = {};
  const slotTotals = {POR:config.slotPOR, DIF:config.slotDIF, CEN:config.slotCEN, ATT:config.slotATT};
  const nTeams = num(config.partecipanti,8);
  ROLES.forEach(r=>{
    const all = players.filter(p=>p.ruolo===r);
    const avail = all.filter(p=>p.stato==="disponibile");
    const totalSlotsLeague = slotTotals[r]*nTeams;
    const boughtCount = all.filter(p=>p.stato==="acquistato").length;
    const slotsLeft = Math.max(totalSlotsLeague - boughtCount, 0);
    const highTierAvail = avail.filter(p=> (p.__score||0) >= 70).length;
    const sortedAvail = avail.slice().sort((a,b)=>(b.__score||0)-(a.__score||0));
    let scoreGap = 0;
    if(sortedAvail.length>=2){
      scoreGap = (sortedAvail[0].__score||0) - (sortedAvail[Math.min(4,sortedAvail.length-1)].__score||0);
    }
    const ratio = slotsLeft>0 ? avail.length/slotsLeft : 2;
    let idx = 0;
    idx += clamp((2-ratio)*35,0,45);
    idx += clamp((slotsLeft>0? (1-(highTierAvail/Math.max(slotsLeft,1))):0)*30,0,30);
    idx += clamp((10-scoreGap)*1.5, 0, 25);
    idx = clamp(idx,0,100);
    let livello = "bassa";
    if(idx>=75) livello="critica"; else if(idx>=55) livello="alta"; else if(idx>=35) livello="media";
    res[r] = {indice:round1(idx), livello, disponibili:avail.length, slotsRimasti:slotsLeft, highTier:highTierAvail, scoreGap:round1(scoreGap)};
  });
  return res;
}

export function validateInflationEvent(h, playersById, teamsById){
  const reasons = [];

  if(!teamsById.has(h.teamId)) reasons.push("squadra inesistente");
  if(!ROLES.includes(h.ruolo)) reasons.push("ruolo non valido");
  if(!Number.isInteger(h.prezzo) || h.prezzo<=0) reasons.push("prezzo finale non valido");
  if(!Number.isInteger(h.prezzoObiettivoAlMomento) || h.prezzoObiettivoAlMomento<=0) reasons.push("prezzo obiettivo non valido");

  const currentPlayer = playersById.get(h.playerId);
  if(!currentPlayer){
    reasons.push("giocatore non presente nel dataset corrente");
  } else {
    const coherent = currentPlayer.stato==="acquistato" && currentPlayer.acquistatoDa===h.teamId &&
      currentPlayer.ruolo===h.ruolo && currentPlayer.prezzoFinale===h.prezzo &&
      !!currentPlayer.purchaseId && currentPlayer.purchaseId===h.purchaseId;
    if(!coherent) reasons.push("acquisto non coerente con il giocatore corrente");
  }

  return {ok: reasons.length===0, reasons};
}

export function computeInflation(purchaseLedger, players, teams, currentEpoch){
  const teamsById = new Map((teams||[]).map(t=>[t.id, t]));
  const playersById = new Map((players||[]).map(p=>[p.id, p]));
  const epoch = num(currentEpoch,0);
  const valid = [];
  const excluded = [];
  const seenPurchaseIds = new Set();
  (purchaseLedger||[]).forEach(entry=>{
    const purchaseId = entry.purchaseId;
    if(purchaseId && seenPurchaseIds.has(purchaseId)){
      excluded.push({purchaseId, playerId:entry.playerId, reasons:["identificativo di acquisto duplicato nel registro"]});
      return;
    }
    if(purchaseId) seenPurchaseIds.add(purchaseId);
    if(entry.annullato===true){
      excluded.push({purchaseId, playerId:entry.playerId, reasons:[entry.annullatoMotivo==="recovery"?"acquisto annullato dal recupero":"acquisto annullato"]});
      return;
    }
    if(num(entry.auctionEpoch,0) !== epoch){
      excluded.push({purchaseId, playerId:entry.playerId, reasons:["appartiene a una sessione d'asta precedente"]});
      return;
    }
    const check = validateInflationEvent(entry, playersById, teamsById);
    if(!check.ok) excluded.push({purchaseId, playerId:entry.playerId, reasons:check.reasons});
    else valid.push(entry);
  });
  function ratioAvg(arr){
    if(!arr.length) return 1;
    const s = arr.reduce((a,h)=> a + (h.prezzo/h.prezzoObiettivoAlMomento), 0);
    return s/arr.length;
  }
  const overall = ratioAvg(valid);
  const byRole = {}; ROLES.forEach(r=> byRole[r]=ratioAvg(valid.filter(h=>h.ruolo===r)));
  const byTier = {}; TIER_ORDER.forEach(t=> byTier[t]=ratioAvg(valid.filter(h=>h.fascia===t)));
  const recent = ratioAvg(valid.slice(-5));
  return {overall: round1(overall*100)/100, byRole, byTier, recent: round1(recent*100)/100,
    count:valid.length, excludedCount:excluded.length, excluded};
}

/* Prezzi consigliati */
export function computePricesForRole(players, ruolo, config, budgetSplit, scarcityByRole, inflation, settings, rosterAggregate){
  const list = players.filter(p=>p.ruolo===ruolo && p.stato!=="acquistato");
  const slotTotals = {POR:config.slotPOR, DIF:config.slotDIF, CEN:config.slotCEN, ATT:config.slotATT}[ruolo];
  const nTeams = num(config.partecipanti,8);
  const totalBudgetLeague = num(config.budget,500)*nTeams;
  const roleBudgetPct = num(budgetSplit[ruolo],25)/100;
  const roleBudgetLeagueTotal = totalBudgetLeague*roleBudgetPct;
  const boughtInRole = players.filter(p=>p.ruolo===ruolo && p.stato==="acquistato");
  const spentInRole = boughtInRole.reduce((a,p)=>a+num(p.prezzoFinale,0),0);
  const remainingRoleBudget = Math.max(roleBudgetLeagueTotal - spentInRole, 0);
  const totalSlotsRole = slotTotals*nTeams;
  const slotsLeftRole = Math.max(totalSlotsRole - boughtInRole.length, 0);
  const scarcity = scarcityByRole[ruolo] || {indice:0,livello:"bassa"};
  const scarcityMult = 1 + (scarcity.indice/100)*0.5*num(settings.coeffScarsita,1);
  const rawInflation = inflation.byRole[ruolo] || inflation.overall || 1;
  const adjustedInflation = 1 + (rawInflation - 1) * num(settings.coeffInflazione, 1);
  const infl = settings.correzioneInflazioneAttiva ? clamp(adjustedInflation, 0.5, num(settings.limiteInflazione,1.8)) : 1;
  const totalWeight = list.reduce((a,p)=> a + Math.pow(Math.max(p.__score||1,1),1.8), 0) || 1;

  list.forEach(p=>{
    const w = Math.pow(Math.max(p.__score||1,1),1.8);
    const shareBase = (w/totalWeight) * remainingRoleBudget;
    const riskMult = 1 - clamp(((p.__sub&&p.__sub.rischio)||0)/100,0,1)*0.25;
    let obiettivo = shareBase * riskMult;
    let massimo = obiettivo * scarcityMult * infl;
    obiettivo = Math.max(obiettivo, config.prezzoMinimo);
    massimo = Math.max(massimo, obiettivo, config.prezzoMinimo);

    if(settings.budgetMinimoProtetto && slotsLeftRole>0){
      const reserveForOthers = Math.max(slotsLeftRole-1,0)*config.prezzoMinimo;
      const capForThis = Math.max(remainingRoleBudget - reserveForOthers, config.prezzoMinimo);
      massimo = Math.min(massimo, capForThis);
      obiettivo = Math.min(obiettivo, massimo);
    }
    const minimoInteressante = Math.max(config.prezzoMinimo, Math.round(obiettivo*0.55));
    p.__prezzoObiettivo = Math.round(obiettivo);
    p.__prezzoMassimo = Math.round(massimo);
    p.__prezzoMinimoInteressante = Math.round(minimoInteressante);
    p.__prezzoSpiegazione = spiegazionePrezzo(p, scarcity, riskMult, slotsLeftRole, config.prezzoMinimo);
  });
}

export function spiegazionePrezzo(p, scarcity, riskMult, slotsLeftRole, prezzoMinimo){
  const riskLabel = riskMult<0.85 ? "rischio alto" : riskMult<0.95 ? "rischio medio" : "rischio basso";
  const reserve = Math.max(slotsLeftRole-1,0)*prezzoMinimo;
  const massimo = effectiveMaxPrice(p);
  const manualNote = hasManualMaxPrice(p) ? ` (override manuale; automatico ${p.__prezzoMassimo})` : "";
  return `Prezzo massimo ${massimo}${manualNote} perché il giocatore ha score ${p.__score>=70?"elevato":p.__score>=50?"medio":"contenuto"} `+
    `(${p.__score}), scarsità ${scarcity.livello} nel ruolo e ${riskLabel}. `+
    (reserve>0 ? `Il limite protegge ${reserve} crediti necessari per completare gli slot rimanenti del ruolo.` : `Non ci sono altri slot del ruolo da proteggere al momento.`);
}

/* Pipeline completa: applica score, fasce, scarsità, prezzi a tutta la lista giocatori (muta copie). */
export function runPipeline(players, config, weights, tierThresholds, budgetSplit, settings, purchaseLedger, teams, auctionEpoch){
  const scarcityByRole = computeScarcityByRole(players, config);
  players.forEach(p=>{
    const r = ROLES.includes(p.ruolo)?p.ruolo:"CEN";
    p.__scarsitaScoreBoost = (scarcityByRole[r]?.indice)||0;
  });
  const gstatsByRole = {};
  ROLES.forEach(r => { gstatsByRole[r] = roleGroupStats(players, r); });
  players.forEach(p=>{
    const r = ROLES.includes(p.ruolo) ? p.ruolo : "CEN";
    const {score, sub} = computeScore(p, gstatsByRole[r], weights, config);
    p.__score = score; p.__sub = sub;
    const auto = effectiveTier(score, tierThresholds);
    p.fasciaAutomatica = auto;
    p.__fasciaEffettiva = p.fasciaManuale || auto;
  });
  const inflation = computeInflation(purchaseLedger||[], players, teams, auctionEpoch);
  ROLES.forEach(r=> computePricesForRole(players, r, config, budgetSplit, scarcityByRole, inflation, settings));
  return {scarcityByRole, inflation};
}
